import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import { useRef } from "react";

import { getNextCommunityEvent } from "../events/eventsConfig";

import { AucklandClock } from "./AucklandClock";
import { VideoMontage } from "./VideoMontage";

const EASE = [0.22, 1, 0.36, 1] as const;

/* The plate the film settles into — one set of insets shared by the
   clip-path animation, the hairline frame, and the caption. */
const PLATE = { top: 10, right: 15, bottom: 16, left: 15 };

/**
 * The set piece. The hero pins for a second viewport of scroll while
 * the full-bleed footage closes down into a framed plate on paper —
 * cinema becomes the book the rest of the page is set in. Title and
 * ledger hand off first; the frame and caption arrive as the film
 * settles into its mat.
 *
 * Under reduced motion the section is a single static viewport with
 * no pin and no morph.
 */
export function Hero() {
  const reduce = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  const titleOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const titleY = useTransform(scrollYProgress, [0, 0.3], [0, -56]);
  const cueOpacity = useTransform(scrollYProgress, [0, 0.1], [1, 0]);
  const scrimOpacity = useTransform(scrollYProgress, [0.2, 0.7], [1, 0.15]);
  const clipPath = useTransform(scrollYProgress, (p) => {
    const t = Math.min(1, Math.max(0, (p - 0.22) / 0.5));
    const e = 1 - Math.pow(1 - t, 3);
    return `inset(${PLATE.top * e}% ${PLATE.right * e}% ${PLATE.bottom * e}%  ${PLATE.left * e}%)`;
  });
  const frameOpacity = useTransform(scrollYProgress, [0.62, 0.82], [0, 1]);
  const captionOpacity = useTransform(scrollYProgress, [0.72, 0.9], [0, 1]);

  const nextEvent = getNextCommunityEvent();

  return (
    <section
      ref={sectionRef}
      className={`relative bg-cream ${reduce ? "h-[100svh]" : "h-[240vh]"}`}
    >
      <div className="sticky top-0 h-[100svh] overflow-hidden">
        {/* Footage — closes down from full bleed into the plate.
            `isolate` + explicit z-layers keep the <video> compositor
            layer under the scrim and type during paint. */}
        <motion.div
          className="absolute inset-0 isolate"
          style={reduce ? undefined : { clipPath }}
          aria-hidden
        >
          <div className="video-breath absolute inset-0 z-0">
            <VideoMontage />
          </div>
          <motion.div
            className="pointer-events-none absolute inset-0 z-10"
            style={{
              opacity: reduce ? 1 : scrimOpacity,
              background:
                "linear-gradient(180deg, rgba(46,32,22,0.4) 0%, rgba(46,32,22,0.16) 40%, rgba(50,34,22,0.36) 68%, rgba(54,36,23,0.72) 100%)",
            }}
          />
        </motion.div>

        {/* The mat — hairline frame and caption that receive the film. */}
        {!reduce && (
          <>
            <motion.div
              className="pointer-events-none absolute border border-(--color-hairline-strong)"
              style={{
                top: `calc(${PLATE.top}% - 0.5rem)`,
                right: `calc(${PLATE.right}% - 0.5rem)`,
                bottom: `calc(${PLATE.bottom}% - 0.5rem)`,
                left: `calc(${PLATE.left}% - 0.5rem)`,
                opacity: frameOpacity,
              }}
              aria-hidden
            />
            <motion.p
              className="plate-caption pointer-events-none absolute"
              style={{
                left: `${PLATE.left}%`,
                top: `calc(${100 - PLATE.bottom}% + 0.875rem)`,
                opacity: captionOpacity,
              }}
            >
              First light · where the practice begins
            </motion.p>
          </>
        )}

        {/* Title block — hands off upward as the film settles. */}
        <motion.div
          className="relative z-20 mx-auto flex h-[100svh] max-w-6xl flex-col justify-end px-7 pt-32 pb-24 md:px-10 md:pb-28"
          style={reduce ? undefined : { opacity: titleOpacity, y: titleY }}
        >
          <motion.div
            className="flex flex-wrap items-center gap-x-3 gap-y-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
          >
            <span className="h-px w-10 bg-gold-light" />
            <span className="text-[0.7rem] font-semibold tracking-[0.32em] text-warm-white/90 uppercase">
              Vihangam Yog New Zealand
            </span>
            <AucklandClock className="ml-auto hidden text-[0.7rem] font-medium tracking-[0.18em] text-warm-white/70 uppercase sm:inline" />
          </motion.div>

          {/* Evening dims the way evening does — light, not ink,
              tells the two halves of the day apart. */}
          <h1
            className="font-display mt-6 max-w-4xl text-warm-white"
            style={{
              fontSize: "clamp(2.5rem, 5.6vw, 5rem)",
              lineHeight: 1.15,
              letterSpacing: "0.004em",
              textShadow: "0 2px 24px rgba(45,31,21,0.3)",
            }}
          >
            {[
              { text: "Ten minutes in the morning.", tone: "" },
              { text: "Ten in the evening.", tone: "text-warm-white/60" },
            ].map((line, i) => (
              <span
                key={line.text}
                className="block overflow-hidden pb-[0.08em]"
              >
                <motion.span
                  className={`block ${line.tone}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{
                    duration: 1.1,
                    delay: 0.75 + i * 0.16,
                    ease: EASE,
                  }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, delay: 1.4, ease: EASE }}
          >
            <a
              href="/events"
              className="inline-flex items-center gap-2.5 rounded-full bg-gold-light px-7 py-3.5 text-sm font-semibold tracking-wide text-warm-brown transition-colors duration-300 hover:bg-peach-deep"
            >
              See all events
              <span aria-hidden>→</span>
            </a>
          </motion.div>

          {/* Next session — one fact-led ledger line on a hairline. */}
          {nextEvent ? (
            <motion.a
              href={`/events/${nextEvent.slug}`}
              className="group mt-12 flex flex-wrap items-baseline gap-x-6 gap-y-2 border-t border-warm-white/25 pt-5 text-warm-white/85 transition-colors duration-300 hover:text-warm-white"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.0, delay: 1.75, ease: EASE }}
            >
              <span className="text-[0.65rem] font-semibold tracking-[0.28em] text-gold-light uppercase">
                Next session
              </span>
              <span className="text-[15px] font-medium tracking-wide md:text-base">
                {nextEvent.title}
                {nextEvent.venue?.suburb ? ` · ${nextEvent.venue.suburb}` : ""}
              </span>
              <span className="text-sm text-warm-white/65 md:text-[15px]">
                {nextEvent.dateLabel} · {nextEvent.timeLabel} · Free
              </span>
              <span className="ml-auto hidden items-center gap-2 text-sm font-medium tracking-wide sm:inline-flex">
                Register
                <span
                  className="transition-transform duration-300 group-hover:translate-x-0.5"
                  aria-hidden
                >
                  →
                </span>
              </span>
            </motion.a>
          ) : null}
        </motion.div>

        {/* Scroll cue — breathes at the site's cadence, first to leave.
            Outer layer owns the entrance fade; inner layer owns the
            scroll-driven exit so the two never fight. */}
        <motion.div
          className="pointer-events-none absolute bottom-7 left-1/2 z-20 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.0, delay: 2.2, ease: EASE }}
          aria-hidden
        >
          <motion.div style={reduce ? undefined : { opacity: cueOpacity }}>
            <span className="cue-breath block h-10 w-px bg-warm-white/70" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
